import { useState, useEffect, useCallback } from 'react';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Title,
  Grid,
  TextInput,
  Button,
  Table,
  Pagination,
  Group,
  Box,
  Text,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconDownload, IconFolderOpen, IconAlertTriangle } from '@tabler/icons-react';

import * as actions from '../../../actions/ArquivosGeraisFinancas';
import ListLoadingOverlay from '../../../components/ListLoadingOverlay';
import { TableSkeleton } from '../../../components/TableSkeleton';
import EmptyState from '../../../components/EmptyState';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { qsNumber, qsString } from '../../../utils/qsHelpers';

const DEFAULTS = { filterText: '', pageSize: 10, page: 1 };

interface SearchFilters { SEARCH?: string }

export default function ArquivosGeraisFinancasListView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useAppSelector((state) => state.arquivosGeraisFinancas.results);
  const listError = useAppSelector((state) => state.arquivosGeraisFinancas.error);
  const loading = useAppSelector((state) => state.container.loading);
  const publicSettings = useAppSelector((state) => state.publicSettings.settings);

  const [searchValue, setSearchValue] = useState('');
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState<number>(DEFAULTS.page);
  const [pageSize] = useState<number>(DEFAULTS.pageSize);

  const doSearch = useCallback((fText: string, pg: number) => {
    const query: Record<string, unknown> = { page: pg, pageSize };
    if (fText) query.q = fText;
    navigate('/arquivos-gerais-financas?' + qs.stringify(query));
  }, [navigate, pageSize]);

  const executeSearch = useCallback(() => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });

    const pg = qsNumber(query.page, DEFAULTS.page);
    const ps = qsNumber(query.pageSize, DEFAULTS.pageSize);
    const fText = qsString(query.q, DEFAULTS.filterText);

    const filters: SearchFilters = {};
    if (fText.length > 0) filters.SEARCH = fText;

    setPage(pg);
    setFilterText(fText);
    if (fText) setSearchValue(fText);

    dispatch((actions as any).list({ page: pg, pageSize: ps, filterText: fText, filters }));
  }, [location.search, dispatch]);

  useEffect(() => { executeSearch(); }, [location.key]);

  const clickSearch = () => {
    if (!searchValue || searchValue.trim().length === 0) {
      notifications.show({ message: 'É necessário informar um valor para busca.', color: 'yellow' });
      return;
    }
    setPage(1);
    setFilterText(searchValue);
    doSearch(searchValue, 1);
  };

  const total = !results ? 0 : results.total;
  const dataSource = !results ? [] : results.items;
  const menorRnum = ((page - 1) * pageSize) + 1;
  let maoirRnum = page * pageSize;
  if (results) maoirRnum = maoirRnum < results.total ? maoirRnum : results.total;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const filterMessage = filterText?.length > 0 ? `Filtrado por "${filterText}"` : null;

  return (
    <Box>
      <Title order={1}>Listar {publicSettings?.arquivosGeraisAbaName}</Title>

      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, md: 7 }} offset={{ base: 0, md: 2 }}>
          <TextInput
            disabled={loading}
            autoFocus
            maxLength={300}
            placeholder="Título e nome do arquivo"
            leftSection={<IconSearch size={16} />}
            value={searchValue || ''}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') clickSearch(); }}
            rightSectionWidth={92}
            rightSection={
              <Button size="xs" onClick={clickSearch} leftSection={<IconSearch size={14} />}>
                Buscar
              </Button>
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }} offset={{ base: 0, md: 2 }}>
          <Group justify="space-between">
            <Text fw={600}>{filterMessage}</Text>
            {results && results.total > 0 && (
              <Text size="sm" c="dimmed">
                Listando {menorRnum} - {maoirRnum} de {results.total} registros.
              </Text>
            )}
          </Group>
        </Grid.Col>
      </Grid>

      <Box mt="md">
        <ListLoadingOverlay loading={loading}>
          {(!loading && listError) ? (
            <EmptyState
              label="Não foi possível carregar os arquivos"
              submessage={listError}
              icon={IconAlertTriangle}
              mih={240}
            />
          ) : (!loading && results && dataSource.length === 0) ? (
            <EmptyState
              label="Nenhum arquivo encontrado"
              submessage={filterMessage ? 'Tente outro termo de busca.' : 'Use a busca acima para começar.'}
              icon={IconFolderOpen}
              mih={240}
            />
          ) : (
            <>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Título</Table.Th>
                    <Table.Th style={{ width: '40%' }}>Nome do arquivo</Table.Th>
                    <Table.Th style={{ width: 100 }}>Ações</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading && dataSource.length === 0 ? (
                    <TableSkeleton columns={['82%', '76%', '52%']} />
                  ) : dataSource.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <Text size="sm" lineClamp={1} title={item.name}>{item.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1} title={item.file?.name}>{item.file?.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Download">
                          <ActionIcon variant="subtle" onClick={() => dispatch((actions as any).download(item.fileId))}>
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    total={totalPages}
                    value={page}
                    onChange={(p) => { setPage(p); doSearch(filterText, p); }}
                  />
                </Group>
              )}
            </>
          )}
        </ListLoadingOverlay>
      </Box>
    </Box>
  );
}
